import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Section, Field, Money, DurumRozeti, grid } from '../bakim-onarim/DetayParcalari';
import { talepYeriLabel, odenekTuruLabel, durumLabel, fmtTarih, toplamOdenek, toplamHakedis, toTLString, parseTL, yuzde, DURUM_TONE } from '../odenek/finansOrtak';

function PdfIcon() {
  return (
    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

// Hakediş bilgi kartı: hakediş + bağlı ödenek + işin hakediş özeti (İhaleler detay penceresiyle aynı düzen)
export function HakedisDetailDialog({ open, onOpenChange, hakedis: h, odenek, hakedisler = [], displayMoney, onEdit, onExportPDF, canEdit = true }) {
  if (!h) return null;
  const isinHakedisleri = hakedisler.filter(x => x.odenek_id === h.odenek_id);
  const sira = isinHakedisleri.findIndex(x => x.id === h.id) + 1;
  const toplamOd = odenek ? toplamOdenek(odenek) : 0;
  const hakToplam = toplamHakedis(isinHakedisleri);
  const buTutar = parseTL(h.talep_tutari);
  const kalan = toplamOd - hakToplam;
  const money = (n) => displayMoney(toTLString(n));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="hakedis-detail-dialog">
        <DialogHeader>
          <DialogTitle className="text-blue-400 flex flex-wrap items-center gap-2" data-testid="hakedis-detail-title">
            Hakediş Detayları: {h.hakedis_no || '-'}
            {sira > 0 && <Badge className="bg-gray-800 text-gray-200 border-gray-700 text-[11px]" data-testid="hakedis-detail-sira">{sira}. / {isinHakedisleri.length} hakediş</Badge>}
          </DialogTitle>
          <DialogDescription className="text-gray-400" data-testid="hakedis-detail-isin-adi">{h.odenek_isin_adi || odenek?.isin_adi || '-'}</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <Section no="1" title="Hakediş Bilgileri" testId="hakedis-detail-section-1">
            <div className={grid}>
              <Field label="Hakediş No" value={h.hakedis_no} testId="hakedis-detail-no" />
              <Field label="Yıl" value={h.yil} testId="hakedis-detail-yil" />
              <Field label="Talep Yeri" value={talepYeriLabel(h.talep_yeri)} testId="hakedis-detail-talep-yeri" />
              <Field label="Talep Tarihi" value={fmtTarih(h.talep_tarihi)} testId="hakedis-detail-talep-tarihi" />
              <Field label="Talep Yazısı Sayı/No" value={h.talep_yazisi_sayi_no} testId="hakedis-detail-talep-yazisi" />
            </div>
          </Section>

          <Section no="2" title="Tutar ve Ödeme Durumu" testId="hakedis-detail-section-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <Money label="Talep Tutarı" value={displayMoney(h.talep_tutari)} testId="hakedis-detail-tutar" tone="bg-emerald-900/20 border border-emerald-700/40" />
              <div className="flex items-center justify-between bg-gray-800/50 p-2 rounded" data-testid="hakedis-detail-durum">
                <span className="text-gray-400">Ödeme Durumu:</span> <DurumRozeti value={h.odeme_durumu} label={durumLabel(h.odeme_durumu)} toneMap={DURUM_TONE} testId="hakedis-detail-durum-rozet" />
              </div>
            </div>
          </Section>

          <Section no="3" title="Bağlı Ödenek" testId="hakedis-detail-section-3">
            {odenek ? (
              <div className={grid}>
                <Field label="İşin Adı" value={odenek.isin_adi} className="col-span-2 md:col-span-3" testId="hakedis-detail-odenek-isin-adi" />
                <Field label="Yıl" value={odenek.yil} testId="hakedis-detail-odenek-yil" />
                <Field label="Ödenek Türü" value={odenekTuruLabel(odenek.odenek_turu)} testId="hakedis-detail-odenek-turu" />
                <Field label="Talep Yeri" value={talepYeriLabel(odenek.talep_yeri)} testId="hakedis-detail-odenek-talep-yeri" />
                <Field label="Ödenek Talep Tutarı" value={displayMoney(odenek.talep_tutari)} testId="hakedis-detail-odenek-tutar" />
                <Field label="Toplam Ödenek (ek dahil, iptal hariç)" value={money(toplamOd)} testId="hakedis-detail-odenek-toplam" />
                <div data-testid="hakedis-detail-odenek-durum"><span className="text-gray-500">Ödenek Durumu:</span> <DurumRozeti value={odenek.odenek_durumu} label={durumLabel(odenek.odenek_durumu)} toneMap={DURUM_TONE} /></div>
              </div>
            ) : (
              <p className="text-sm text-gray-500" data-testid="hakedis-detail-odenek-yok">Bağlı ödenek kaydı bulunamadı (silinmiş olabilir). İş: {h.odenek_isin_adi || '-'}</p>
            )}
          </Section>

          <Section no="4" title="İşin Hakediş Özeti" testId="hakedis-detail-section-4">
            <div className="space-y-3">
              <div className="bg-blue-900/20 border border-blue-500 rounded-lg p-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <Money label="Hakediş Sayısı" value={String(isinHakedisleri.length)} testId="hakedis-detail-ozet-sayi" tone="bg-blue-900/30" />
                  <Money label="Toplam Hakediş" value={money(hakToplam)} testId="hakedis-detail-ozet-toplam" tone="bg-blue-900/30" />
                  <Money label="Bu Hakedişin Payı" value={yuzde(buTutar, toplamOd || hakToplam)} testId="hakedis-detail-ozet-pay" tone="bg-blue-900/30" />
                  <Money label="Kalan Ödenek" value={toplamOd > 0 ? `${kalan < 0 ? '-' : ''}${money(Math.abs(kalan))}` : ''} testId="hakedis-detail-ozet-kalan" tone={kalan < 0 ? 'bg-red-900/30' : 'bg-blue-900/30'} />
                </div>
              </div>
              {isinHakedisleri.length > 1 && (
                <div className="overflow-x-auto rounded-lg border border-gray-800">
                  <table className="w-full text-sm" data-testid="hakedis-detail-diger-tablosu">
                    <thead className="bg-gray-800 text-gray-400 text-xs uppercase">
                      <tr><th className="px-3 py-2 text-left">#</th><th className="px-3 py-2 text-left">Hakediş No</th><th className="px-3 py-2 text-left">Talep Tarihi</th><th className="px-3 py-2 text-right">Tutar</th><th className="px-3 py-2 text-left">Durum</th></tr>
                    </thead>
                    <tbody>
                      {isinHakedisleri.map((x, i) => (
                        <tr key={x.id} className={`border-t border-gray-800 ${x.id === h.id ? 'bg-blue-900/20' : ''}`}>
                          <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                          <td className="px-3 py-2 text-white">{x.hakedis_no || '-'}{x.id === h.id && <span className="ml-2 text-[10px] text-blue-300">(bu kayıt)</span>}</td>
                          <td className="px-3 py-2 text-white">{fmtTarih(x.talep_tarihi) || '-'}</td>
                          <td className="px-3 py-2 text-right text-white font-medium">{displayMoney(x.talep_tutari) || '-'}</td>
                          <td className="px-3 py-2"><DurumRozeti value={x.odeme_durumu} label={durumLabel(x.odeme_durumu)} toneMap={DURUM_TONE} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {(h.last_modified_by_name || h.last_modified_at) && (
                <p className="text-xs text-gray-500" data-testid="hakedis-detail-son-duzenleyen">
                  Son düzenleyen: {h.last_modified_by_name || '-'}{h.last_modified_at ? ` · ${new Date(h.last_modified_at).toLocaleString('tr-TR')}` : ''}
                </p>
              )}
            </div>
          </Section>
        </div>

        <DialogFooter>
          {canEdit && (
            <Button onClick={() => { onOpenChange(false); onEdit(h); }} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="hakedis-detail-edit-btn">Düzenle</Button>
          )}
          <Button onClick={() => onExportPDF(h)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="hakedis-detail-pdf-btn"><PdfIcon />PDF İndir</Button>
          <Button onClick={() => onOpenChange(false)} className="bg-gray-800 text-white hover:bg-gray-700" data-testid="hakedis-detail-close-btn">Kapat</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
